import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, SERVICE_TYPES, PAYMENT_METHODS, money } from "../lib/api";
import { TopBar } from "../components/TopBar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { ArrowLeft, Loader2, User, Wrench, Receipt, Save, ChevronsUpDown } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  customer: { name: "", phone: "", email: "", billing_address: "", service_address: "" },
  invoice_date: today(),
  service_date: today(),
  service_type: "single_stump",
  description: "",
  quantity: 1,
  amount: 0,
  tax_enabled: false,
  tax_percentage: 8.0,
  manual_override: false,
  total: 0,
  status: "unpaid",
  overdue: false,
  payment_method: "",
  notes: "",
};

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 bg-[#F8F6F0] border-b border-slate-200">
        <Icon className="h-4 w-4 text-[#1B3B2B]" />
        <h2 className="font-headline font-semibold text-slate-800 text-sm tracking-wide">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

const inputCls = "h-12 text-base px-4 rounded-lg border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20 font-medium";

export default function InvoiceForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [sameAddress, setSameAddress] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const clientKey = useRef(crypto.randomUUID());
  const savedRef = useRef(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/invoices/${id}`).then((r) => {
        const d = r.data;
        setForm({
          customer: d.customer || emptyForm.customer,
          invoice_date: (d.invoice_date || today()).slice(0, 10),
          service_date: (d.service_date || today()).slice(0, 10),
          service_type: d.service_type || "single_stump",
          description: d.description || "",
          quantity: d.quantity ?? 1,
          amount: d.amount ?? 0,
          tax_enabled: !!d.tax_enabled,
          tax_percentage: d.tax_percentage ?? 8.0,
          manual_override: !!d.manual_override,
          total: d.total ?? 0,
          status: d.status || "unpaid",
          overdue: !!d.overdue,
          payment_method: d.payment_method || "",
          notes: d.notes || "",
        });
        const c = d.customer || {};
        setSameAddress(!c.service_address || c.service_address === c.billing_address);
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const setCustomer = (patch) => setForm((f) => ({ ...f, customer: { ...f.customer, ...patch } }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const lineTotal = useMemo(
    () => Number(form.quantity || 0) * Number(form.amount || 0),
    [form.quantity, form.amount]
  );
  const taxAmount = useMemo(
    () => (form.tax_enabled ? (lineTotal * Number(form.tax_percentage || 0)) / 100 : 0),
    [form.tax_enabled, form.tax_percentage, lineTotal]
  );
  const computedTotal = useMemo(() => lineTotal + taxAmount, [lineTotal, taxAmount]);

  useEffect(() => {
    if (!form.manual_override) set({ total: Number(computedTotal.toFixed(2)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedTotal, form.manual_override]);

  const pickCustomer = (c) => {
    setCustomer({
      name: c.name || "",
      phone: c.phone || "",
      email: c.email || "",
      billing_address: c.billing_address || "",
      service_address: c.service_address || c.billing_address || "",
    });
    setSameAddress(!c.service_address || c.service_address === c.billing_address);
    setCustOpen(false);
  };

  const save = useCallback(async () => {
    if (savedRef.current) return;
    if (!form.customer.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    savedRef.current = true;
    setSaving(true);
    const customer = { ...form.customer };
    if (sameAddress) customer.service_address = customer.billing_address;
    const payload = {
      ...form,
      customer,
      quantity: Number(form.quantity || 0),
      amount: Number(form.amount || 0),
      tax_percentage: Number(form.tax_percentage || 0),
      total: Number(form.total || 0),
      client_key: isEdit ? undefined : clientKey.current,
    };
    try {
      let res;
      if (isEdit) res = await api.put(`/invoices/${id}`, payload);
      else res = await api.post("/invoices", payload);
      toast.success(isEdit ? "Invoice updated" : "Invoice saved");
      navigate(`/invoice/${res.data.id}`);
    } catch (e) {
      savedRef.current = false;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [form, sameAddress, isEdit, id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F6F0]">
        <TopBar />
        <div className="flex justify-center py-24">
          <Loader2 className="h-7 w-7 animate-spin text-[#1B3B2B]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6F0]">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-32">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1B3B2B] mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <h1 className="font-headline text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-5">
          {isEdit ? "Edit Invoice" : "New Invoice"}
        </h1>

        <div className="space-y-4 animate-fade-up">
          <SectionCard icon={User} title="CUSTOMER INFORMATION">
            <div>
              <Label className="text-slate-700 font-semibold text-sm">Customer Name</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  data-testid="customer-name-input"
                  value={form.customer.name}
                  onChange={(e) => setCustomer({ name: e.target.value })}
                  placeholder="Full name"
                  className={`${inputCls} flex-1`}
                />
                <Popover open={custOpen} onOpenChange={setCustOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      data-testid="repeat-customer-button"
                      variant="outline"
                      className="h-12 px-3 border-slate-300 shrink-0"
                      title="Pick repeat customer"
                    >
                      <ChevronsUpDown className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[280px]" align="end">
                    <Command>
                      <CommandInput placeholder="Search customers…" />
                      <CommandList>
                        <CommandEmpty>No customers yet.</CommandEmpty>
                        <CommandGroup>
                          {customers.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={c.name}
                              data-testid="repeat-customer-option"
                              onSelect={() => pickCustomer(c)}
                            >
                              <div>
                                <p className="font-medium">{c.name}</p>
                                <p className="text-xs text-slate-500">{c.phone}</p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Phone</Label>
                <Input data-testid="customer-phone-input" value={form.customer.phone}
                  onChange={(e) => setCustomer({ phone: e.target.value })}
                  placeholder="(555) 000-0000" className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Email <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input data-testid="customer-email-input" type="email" value={form.customer.email}
                  onChange={(e) => setCustomer({ email: e.target.value })}
                  placeholder="name@email.com" className={`${inputCls} mt-1.5`} />
              </div>
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-sm">Billing Address</Label>
              <Textarea data-testid="customer-billing-address-input" value={form.customer.billing_address}
                onChange={(e) => setCustomer({ billing_address: e.target.value })}
                placeholder="Street, City" rows={2}
                className="mt-1.5 text-base rounded-lg border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20" />
            </div>

            <div className="flex items-center gap-2.5 pt-1">
              <Checkbox
                id="same-address"
                data-testid="same-address-checkbox"
                checked={sameAddress}
                onCheckedChange={(v) => setSameAddress(!!v)}
              />
              <Label htmlFor="same-address" className="text-sm text-slate-600 font-medium cursor-pointer">
                Service address same as billing
              </Label>
            </div>
            {!sameAddress && (
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Service Address</Label>
                <Textarea data-testid="service-address-input" value={form.customer.service_address}
                  onChange={(e) => setCustomer({ service_address: e.target.value })}
                  placeholder="Where the work is performed" rows={2}
                  className="mt-1.5 text-base rounded-lg border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20" />
              </div>
            )}
          </SectionCard>

          <SectionCard icon={Wrench} title="SERVICE DETAILS">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Invoice Date</Label>
                <Input data-testid="invoice-date-input" type="date" value={form.invoice_date}
                  onChange={(e) => set({ invoice_date: e.target.value })} className={`${inputCls} mt-1.5`} />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Service Date</Label>
                <Input data-testid="service-date-input" type="date" value={form.service_date}
                  onChange={(e) => set({ service_date: e.target.value })} className={`${inputCls} mt-1.5`} />
              </div>
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-sm">Service Type</Label>
              <Select value={form.service_type} onValueChange={(v) => set({ service_type: v })}>
                <SelectTrigger data-testid="service-type-select" className={`${inputCls} mt-1.5`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-sm">Description</Label>
              <Textarea data-testid="service-description-input" value={form.description}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="Describe the work performed" rows={2}
                className="mt-1.5 text-base rounded-lg border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Quantity</Label>
                <Input data-testid="service-quantity-input" type="number" min="0" step="1" value={form.quantity}
                  onChange={(e) => set({ quantity: e.target.value })} className={`${inputCls} mt-1.5 font-mono-data`} />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Unit Price ($)</Label>
                <Input data-testid="service-amount-input" type="number" min="0" step="0.01" value={form.amount}
                  onChange={(e) => set({ amount: e.target.value })} className={`${inputCls} mt-1.5 font-mono-data`} />
              </div>
            </div>
            <div className="flex items-center justify-between px-4 py-3 bg-[#E8F0EC] rounded-lg">
              <span className="text-sm font-semibold text-[#1B3B2B]">Line Total</span>
              <span data-testid="line-total-display" className="font-mono-data font-bold text-[#1B3B2B]">{money(lineTotal)}</span>
            </div>
          </SectionCard>

          <SectionCard icon={Receipt} title="TAX, TOTAL & PAYMENT">
            <div className="flex items-center justify-between">
              <Label className="text-slate-700 font-semibold text-sm">Apply Tax</Label>
              <Switch data-testid="tax-toggle-switch" checked={form.tax_enabled}
                onCheckedChange={(v) => set({ tax_enabled: v })} />
            </div>
            {form.tax_enabled && (
              <div className="flex items-center gap-3">
                <Input data-testid="tax-percentage-input" type="number" min="0" step="0.1" value={form.tax_percentage}
                  onChange={(e) => set({ tax_percentage: e.target.value })}
                  className={`${inputCls} font-mono-data w-28`} />
                <span className="text-sm text-slate-500">% tax = <span className="font-mono-data font-semibold text-slate-700">{money(taxAmount)}</span></span>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <Label className="text-slate-700 font-semibold text-sm">Manual Total Override</Label>
              <Switch data-testid="manual-override-toggle" checked={form.manual_override}
                onCheckedChange={(v) => set({ manual_override: v })} />
            </div>

            <div className="flex items-center justify-between px-4 py-4 bg-[#152E22] rounded-lg">
              <span className="text-sm font-semibold text-emerald-100">Total Due</span>
              {form.manual_override ? (
                <Input data-testid="manual-override-input" type="number" min="0" step="0.01" value={form.total}
                  onChange={(e) => set({ total: e.target.value })}
                  className="h-11 w-40 text-right font-mono-data font-bold text-lg bg-white/95 border-0" />
              ) : (
                <span data-testid="total-display" className="font-mono-data font-extrabold text-2xl text-white">{money(form.total)}</span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Payment Status</Label>
                <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                  <SelectTrigger data-testid="payment-status-select" className={`${inputCls} mt-1.5`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Payment Method</Label>
                <Select value={form.payment_method || "none"} onValueChange={(v) => set({ payment_method: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="payment-method-select" className={`${inputCls} mt-1.5`}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Label className="text-slate-700 font-semibold text-sm">Mark as Overdue</Label>
              <Switch data-testid="overdue-toggle" checked={form.overdue}
                onCheckedChange={(v) => set({ overdue: v })} />
            </div>

            <div>
              <Label className="text-slate-700 font-semibold text-sm">Notes</Label>
              <Textarea data-testid="invoice-notes-input" value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
                placeholder="Internal notes or customer terms" rows={2}
                className="mt-1.5 text-base rounded-lg border-slate-300 focus:border-[#1B3B2B] focus:ring-2 focus:ring-[#1B3B2B]/20" />
            </div>
          </SectionCard>
        </div>
      </main>

      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Total Due</p>
            <p className="font-mono-data font-bold text-lg text-[#1B3B2B] truncate">{money(form.total)}</p>
          </div>
          <Button
            data-testid="save-invoice-button"
            onClick={save}
            disabled={saving}
            className="h-12 px-8 bg-[#1B3B2B] hover:bg-[#254F3A] text-white font-semibold rounded-lg"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {isEdit ? "Update" : "Save Invoice"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
